import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PopularSidebar from '@/components/feature/PopularSidebar';
import { categories } from '@/mocks/categories';
import { articles, popularArticles } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {};
categories.forEach((c) => { categoryNameMap[c.slug] = c.name; });

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <p className="text-foreground-500">文章不存在</p>
          <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 4);

  const catName = categoryNameMap[article.category] || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '黑料网吃瓜',
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料网吃瓜',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliao.com/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://heiliao.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: catName,
        item: `https://heiliao.com/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: catName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />

              <div className="flex flex-col lg:flex-row gap-8 pb-14">
                {/* Article Content */}
                <article className="flex-1 min-w-0">
                  {/* Cover Image */}
                  <div className="aspect-[16/9] rounded-lg overflow-hidden mb-6">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - 黑料网吃瓜`}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <Link
                      to={`/category/${article.category}`}
                      className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {catName}
                    </Link>
                    <span className="text-sm text-foreground-500">{article.publishedAt}</span>
                    <span className="text-sm text-foreground-400 flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line"></i>
                      </div>
                      {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                    </span>
                  </div>

                  {/* Title */}
                  <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                    {article.title}
                  </h1>

                  {/* Tags */}
                  <div className="flex items-center gap-2 flex-wrap mb-8">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Content */}
                  <div
                    className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                      [&_p]:mb-5 [&_p]:text-[15px] [&_p]:leading-[1.85]
                      [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4
                      [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  ></div>
                </article>

                {/* Sidebar */}
                <aside className="w-full lg:w-[320px] shrink-0">
                  <PopularSidebar items={popularArticles} />
                </aside>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section className="pb-14">
                  <h2 className="text-lg font-bold text-foreground-950 mb-5">相关推荐</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden group cursor-pointer transition-all hover:border-primary-200/70"
                      >
                        <div className="aspect-[16/9] overflow-hidden">
                          <img
                            src={ra.coverImage}
                            alt={ra.title}
                            title={`${ra.title} - 黑料网吃瓜`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-bold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                            {ra.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-2">{ra.publishedAt}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}